import { isMobileOrTablet } from '../../../common/core-layer/utils/core.utils'

export const SUPPORT_CHAT_WIDGET_MOUNT_ID = 'clientportal-chatWidget'

/** Stacking order for the third-party launcher — above CP topbar (40) and course overlays. */
export const SUPPORT_CHAT_WIDGET_Z_INDEX = '10000'

/** Set on the mount host after a successful inject so we do not treat another location/widget’s `<chat-widget>` as ours. */
export const SUPPORT_CHAT_WIDGET_EMBED_KEY_ATTR = 'data-cp-support-chat-embed-key'

/** Mobile offsets for LeadConnector launcher and teaser bubble above mobile bottom nav / lesson actions. */
export const SUPPORT_CHAT_MOBILE_BUBBLE_BOTTOM = '84px'
export const SUPPORT_CHAT_MOBILE_PROMPT_BOTTOM = '64px'
export const SUPPORT_CHAT_MOBILE_BUBBLE_SAFE_BOTTOM = 'calc(env(safe-area-inset-bottom, 0px) + 84px)'
export const SUPPORT_CHAT_MOBILE_PROMPT_SAFE_BOTTOM = 'calc(env(safe-area-inset-bottom, 0px) + 64px)'

export function getSupportChatWidgetMountElement(): HTMLElement | null {
  if (typeof document === 'undefined') return null
  const mount = document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)
  return mount instanceof HTMLElement ? mount : null
}

/** Prefer widget under our mount; fall back to document only when this session owns an embed. */
export function findSupportChatWidgetElement(): Element | null {
  const mount = getSupportChatWidgetMountElement()
  const inMount = mount?.querySelector('chat-widget')
  if (inMount) return inMount
  if (getSupportChatMountedEmbedKey()) {
    return document.querySelector('chat-widget')
  }
  return null
}

export function serializeSupportChatEmbedMountKey(locationId: string, widgetId: string): string {
  return JSON.stringify({ locationId, widgetId })
}

export function getSupportChatMountedEmbedKey(): string | null {
  if (typeof document === 'undefined') return null
  const mount = document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)
  return mount?.getAttribute(SUPPORT_CHAT_WIDGET_EMBED_KEY_ATTR) ?? null
}

export function setSupportChatMountedEmbedKey(key: string): void {
  if (typeof document === 'undefined') return
  document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)?.setAttribute(SUPPORT_CHAT_WIDGET_EMBED_KEY_ATTR, key)
}

function clearSupportChatMountedEmbedKey(): void {
  if (typeof document === 'undefined') return
  document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)?.removeAttribute(SUPPORT_CHAT_WIDGET_EMBED_KEY_ATTR)
}

/** True when LiveChat (or similar) has already booted for this portal session — used to skip refetch/re-inject on route changes. */
export function isSupportChatWidgetInDocument(): boolean {
  if (typeof document === 'undefined') return false
  return Boolean(findSupportChatWidgetElement())
}

declare global {
  interface Window {
    /** MutationObserver from `attachSupportChatWidgetDomHooks` while waiting for `<chat-widget>`. */
    __cpSupportChatWidgetMountObserver?: MutationObserver
    __cpSupportChatWidgetMountObserverTimeoutId?: ReturnType<typeof setTimeout>
    /** MutationObserver for mobile bubble/prompt positioning. */
    __cpSupportChatMobilePositionObserver?: MutationObserver
    /** Event listener reference for LC_chatWidgetLoaded cleanup. */
    __cpSupportChatMobilePositionLoadedHandler?: (event: Event) => void
  }
}

function clearSupportChatWidgetDomHookObserver(): void {
  const w = globalThis as unknown as Window
  const timeoutId = w.__cpSupportChatWidgetMountObserverTimeoutId
  if (timeoutId !== undefined) {
    clearTimeout(timeoutId)
    w.__cpSupportChatWidgetMountObserverTimeoutId = undefined
  }
  const mountObserver = w.__cpSupportChatWidgetMountObserver
  if (mountObserver) {
    mountObserver.disconnect()
    w.__cpSupportChatWidgetMountObserver = undefined
  }
}

export function clearSupportChatWidgetMobilePositioning(): void {
  const w = globalThis as unknown as Window
  if (w.__cpSupportChatMobilePositionObserver) {
    w.__cpSupportChatMobilePositionObserver.disconnect()
    w.__cpSupportChatMobilePositionObserver = undefined
  }
  if (w.__cpSupportChatMobilePositionLoadedHandler && typeof window !== 'undefined') {
    window.removeEventListener('LC_chatWidgetLoaded', w.__cpSupportChatMobilePositionLoadedHandler)
    w.__cpSupportChatMobilePositionLoadedHandler = undefined
  }
}

export function clearSupportChatWidgetMount(): void {
  if (typeof document === 'undefined') return

  clearSupportChatWidgetDomHookObserver()
  clearSupportChatWidgetMobilePositioning()

  const widget = findSupportChatWidgetElement()
  const mount = document.getElementById(SUPPORT_CHAT_WIDGET_MOUNT_ID)
  if (mount) {
    clearSupportChatMountedEmbedKey()
    mount.innerHTML = ''
  }

  widget?.remove()
}

function applyMountHostStyles(mount: HTMLElement): void {
  mount.style.setProperty('position', 'fixed')
  mount.style.setProperty('inset', '0')
  mount.style.setProperty('z-index', SUPPORT_CHAT_WIDGET_Z_INDEX)
  mount.style.setProperty('pointer-events', 'none')
}

function applyChatHostStyles(element: Element): void {
  const el = element as HTMLElement
  el.style.setProperty('z-index', SUPPORT_CHAT_WIDGET_Z_INDEX)
  el.style.setProperty('pointer-events', 'auto')
}

function applyElementMobileBottom(el: HTMLElement, standard: string, safe: string): void {
  el.style.setProperty('bottom', standard, 'important')
  try {
    el.style.setProperty('bottom', safe, 'important')
  } catch {
    // env() unsupported in test environments like happy-dom
  }
}

function applyStylesToRoot(root: ParentNode | Element): void {
  if (root instanceof HTMLElement) {
    if (root.classList.contains('lc_text-widget--bubble')) {
      applyElementMobileBottom(root, SUPPORT_CHAT_MOBILE_BUBBLE_BOTTOM, SUPPORT_CHAT_MOBILE_BUBBLE_SAFE_BOTTOM)
    }
    if (root.classList.contains('lc_text-widget--prompt')) {
      applyElementMobileBottom(root, SUPPORT_CHAT_MOBILE_PROMPT_BOTTOM, SUPPORT_CHAT_MOBILE_PROMPT_SAFE_BOTTOM)
    }
    if (root.tagName?.toLowerCase() === 'chat-widget') {
      applyChatHostStyles(root)
    }
  }

  const btn = root.querySelector?.('.lc_text-widget--bubble') as HTMLElement | null
  if (btn?.style?.setProperty) {
    applyElementMobileBottom(btn, SUPPORT_CHAT_MOBILE_BUBBLE_BOTTOM, SUPPORT_CHAT_MOBILE_BUBBLE_SAFE_BOTTOM)
  }

  const prompt = root.querySelector?.('.lc_text-widget--prompt') as HTMLElement | null
  if (prompt?.style?.setProperty) {
    applyElementMobileBottom(prompt, SUPPORT_CHAT_MOBILE_PROMPT_BOTTOM, SUPPORT_CHAT_MOBILE_PROMPT_SAFE_BOTTOM)
  }

  if (root instanceof Element && root.shadowRoot) {
    applyStylesToRoot(root.shadowRoot)
  }
}

export function applySupportChatMobileStyles(container: Element): void {
  applyStylesToRoot(container)

  if (typeof document !== 'undefined') {
    applyStylesToRoot(document)
    document.querySelectorAll('chat-widget').forEach(widget => {
      applyChatHostStyles(widget)
      if (widget.shadowRoot) {
        applyStylesToRoot(widget.shadowRoot)
      }
    })
  }
}

export function applySupportChatWidgetMobilePositioning(container?: Element | null): void {
  if (typeof document === 'undefined') return

  const target = container ?? findSupportChatWidgetElement() ?? getSupportChatWidgetMountElement() ?? document.body
  if (!target) return

  applySupportChatMobileStyles(target)

  const w = globalThis as unknown as Window
  if (w.__cpSupportChatMobilePositionObserver) {
    w.__cpSupportChatMobilePositionObserver.disconnect()
    w.__cpSupportChatMobilePositionObserver = undefined
  }

  if (typeof window !== 'undefined' && !w.__cpSupportChatMobilePositionLoadedHandler) {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<Element>
      const loadedContainer = customEvent.detail ?? findSupportChatWidgetElement() ?? document.querySelector('chat-widget')
      if (loadedContainer) {
        applySupportChatWidgetMobilePositioning(loadedContainer)
      }
    }
    w.__cpSupportChatMobilePositionLoadedHandler = handler
    window.addEventListener('LC_chatWidgetLoaded', w.__cpSupportChatMobilePositionLoadedHandler)
  }

  const observer = new MutationObserver(mutationsList => {
    for (const mutation of mutationsList) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element
          if (
            el.tagName?.toLowerCase() === 'chat-widget' ||
            el.classList?.contains('lc_text-widget--bubble') ||
            el.classList?.contains('lc_text-widget--prompt') ||
            el.querySelector?.('chat-widget') ||
            el.querySelector?.('.lc_text-widget--bubble') ||
            el.querySelector?.('.lc_text-widget--prompt')
          ) {
            applySupportChatMobileStyles(target)
            return
          }
        }
      }
    }
  })

  const observeTarget = target.shadowRoot ?? target
  observer.observe(observeTarget, {
    childList: true,
    subtree: true,
  })

  w.__cpSupportChatMobilePositionObserver = observer
}

/**
 * After postscribe completes, find <chat-widget> (immediate or via MutationObserver) and style it.
 */
export function attachSupportChatWidgetDomHooks(): void {
  if (typeof document === 'undefined') return

  clearSupportChatWidgetDomHookObserver()
  clearSupportChatWidgetMobilePositioning()

  const mount = getSupportChatWidgetMountElement()
  if (mount) {
    applyMountHostStyles(mount)
  }

  const applyHostStyles = (widget: Element) => {
    applyChatHostStyles(widget)
    if (isMobileOrTablet()) {
      applySupportChatWidgetMobilePositioning(widget)
    }
  }

  if (isMobileOrTablet()) {
    applySupportChatWidgetMobilePositioning()
  }

  const existing = findSupportChatWidgetElement()
  if (existing) {
    applyHostStyles(existing)
    return
  }

  const w = globalThis as unknown as Window
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element
          const widget = element.tagName?.toLowerCase() === 'chat-widget' ? element : element.querySelector('chat-widget')

          if (widget) {
            applyHostStyles(widget)
            clearSupportChatWidgetDomHookObserver()
            return
          }
        }
      }
    }
  })

  w.__cpSupportChatWidgetMountObserver = observer
  const observeTarget = mount ?? document.body
  observer.observe(observeTarget, { childList: true, subtree: true })
  w.__cpSupportChatWidgetMountObserverTimeoutId = globalThis.setTimeout(() => {
    clearSupportChatWidgetDomHookObserver()
  }, 10000)
}
